// @ts-strict-ignore
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, CopyButton, PagingSidePanelType, SidePanel, Tooltip } from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { SidePanelHeaderTemplateTwo } from 'components/SidePanel/SidePanelHeaderTemplates/SidePanelHeaderTemplateTwo'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { ContactCase } from 'types/contacts'
import { PaymentContextType } from 'types/payments'
import { highlightRowBasedOnId } from 'utils/table/table'
import { useContactCaseSidePanelHelper } from '../hooks/useContactCaseSidePanelHelper'
import { ContactCaseSidePanelBody } from './ContactCaseSidePanelBody'

export type ContactCaseSummaryProps = {
  isOpen: boolean
  contactCase: ContactCase
  pageBeneficiaries: PagingSidePanelType
  onClose: () => void
}

export const ContactCaseSummary = ({
  isOpen,
  contactCase,
  pageBeneficiaries,
  onClose,
}: ContactCaseSummaryProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)
  const paymentContextType = contactCase?.paymentContext?.type

  const {
    isLoading,
    payment,
    channel,
    isPaymentIn,
    isChannelPayment,
    isDirectDeposit,
    isWithdrawal,
    isError,
  } = useContactCaseSidePanelHelper({
    contactCase,
    paymentContextType,
  })

  const getSecondaryLabel = () => {
    switch (paymentContextType) {
      case PaymentContextType.CHANNEL_PAYMENT:
        return t('contacts.channelPayment')
      case PaymentContextType.PAYMENT_IN:
        return t('contacts.paymentIn')
      case PaymentContextType.DEPOSIT:
        return t('contacts.userDeposit')
      case PaymentContextType.WITHDRAWAL:
        return t('contacts.withdrawal')
      default:
        return
    }
  }

  const primaryLabel = `${contactCase?.amount} ${contactCase?.currencyCode}`
  const showReference = !isLoading && !isError && !isDirectDeposit && !isWithdrawal

  const iconUrl = contactCase?.currencyCode
    ? `${process.env.ICONS_URL}${contactCase.currencyCode?.toLowerCase()}.svg`
    : process.env.ICONS_URL

  return (
    <SidePanel
      id="side-panel-summary-contact-case"
      topOffset={bannerSpacing}
      isOpen={isOpen}
      paging={pageBeneficiaries}
      onClose={onClose}
      activeRowId={contactCase?.externalId}
      onActiveRowChange={highlightRowBasedOnId}
      header={
        <SidePanelHeaderTemplateTwo
          heading={{
            iconUrls: [iconUrl],
            primaryLabel: <h3 className="text-h3 text-foreground-prominent">{primaryLabel}</h3>,
            secondaryLabel: (
              <Box className="md:flex w-full">
                <p className="text-body-regular text-foreground-subdued">{getSecondaryLabel()}</p>
              </Box>
            ),
          }}
        >
          {showReference && contactCase?.externalId && (
            <Box flex width="full" align="center" gapX={4}>
              <span className="text-body-regular text-foreground-subdued text-ellipsis overflow-hidden text-nowrap">
                {isPaymentIn && payment?.reference}
                {isChannelPayment && channel?.reference}
              </span>
              <Tooltip bodyContent={t('copyReference')} type="simple">
                <Box flex align="center">
                  <CopyButton copyText={contactCase?.externalId} />
                </Box>
              </Tooltip>
            </Box>
          )}
        </SidePanelHeaderTemplateTwo>
      }
      body={
        <Box tag="section" width="full" background="standard">
          <ContactCaseSidePanelBody contactCase={contactCase} />
        </Box>
      }
    />
  )
}
